import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  PlusIcon, TrashIcon, FloppyDiskIcon, ArrowClockwiseIcon, WarningIcon, CheckCircleIcon,
} from "@phosphor-icons/react";

const DEFAULTS = [
  { symbol: "EURUSD", stop_loss_pips: 15, take_profit_pips: 30, risk_percent: 1.0, max_positions: 1 },
  { symbol: "XAUUSD", stop_loss_pips: 25, take_profit_pips: 50, risk_percent: 1.0, max_positions: 1 },
  { symbol: "DAX40",  stop_loss_pips: 30, take_profit_pips: 60, risk_percent: 1.0, max_positions: 1 },
  { symbol: "XAGUSD", stop_loss_pips: 20, take_profit_pips: 40, risk_percent: 1.0, max_positions: 1 },
];

const BRIDGE_TOKEN_KEY = "botgenomix_bridge_token";

export default function SeedSymbols() {
  const [rows, setRows] = useState([]);
  const [source, setSource] = useState("default");
  const [updatedAt, setUpdatedAt] = useState(null);
  const [token, setToken] = useState(
    () => window.localStorage.getItem(BRIDGE_TOKEN_KEY) || ""
  );
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null); // { kind: "ok"|"err", text }

  const load = () =>
    api.get("/config/seed-symbols").then((r) => {
      setRows(r.data.symbols || []);
      setSource(r.data.source || "default");
      setUpdatedAt(r.data.updated_at);
    }).catch(console.error);

  useEffect(() => { load(); }, []);

  const update = (i, key, val) => {
    setRows((prev) => prev.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  };

  const addRow = () => {
    setRows((prev) => [...prev, {
      symbol: "", stop_loss_pips: 20, take_profit_pips: 40,
      risk_percent: 1.0, max_positions: 1,
    }]);
  };

  const removeRow = (i) => {
    setRows((prev) => prev.filter((_, idx) => idx !== i));
  };

  const resetDefaults = () => {
    setRows(DEFAULTS.map((r) => ({ ...r })));
    setMessage(null);
  };

  const save = async () => {
    if (!token) {
      setMessage({ kind: "err", text: "Bridge token required. Paste it below and try again." });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const payload = {
        symbols: rows
          .map((r) => ({
            symbol: (r.symbol || "").trim().toUpperCase(),
            stop_loss_pips: Number(r.stop_loss_pips) || 0,
            take_profit_pips: Number(r.take_profit_pips) || 0,
            risk_percent: Number(r.risk_percent) || 0,
            max_positions: Number(r.max_positions) || 1,
          }))
          .filter((r) => r.symbol),
      };
      if (!payload.symbols.length) {
        setMessage({ kind: "err", text: "At least one symbol required." });
        setSaving(false);
        return;
      }
      const res = await api.put("/config/seed-symbols", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      window.localStorage.setItem(BRIDGE_TOKEN_KEY, token);
      setRows(res.data.symbols);
      setSource("custom");
      setUpdatedAt(res.data.updated_at);
      setMessage({ kind: "ok", text: `Saved ${res.data.count} symbols. ai_brain.py picks these up on next cycle.` });
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "save failed";
      setMessage({ kind: "err", text: `Save failed: ${detail}` });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="seed-symbols-page" className="p-4 md:p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] mono uppercase tracking-widest text-[#D6FF38]/70">Config</div>
          <h1 className="text-xl md:text-2xl tracking-tight font-medium mt-1 text-[#F1F5F9]">Seed Symbol Editor</h1>
        </div>
        <div className="text-[11px] mono text-[#64748B]">
          source · <span className="text-[#F1F5F9]">{source}</span>
          {updatedAt && <span> · updated {new Date(updatedAt).toLocaleString()}</span>}
        </div>
      </div>

      <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4 text-[12px] text-[#CBD5E1] leading-relaxed">
        These symbols are used by <code className="mono text-[#D6FF38]">ai_brain.py</code>&apos;s
        forced-pivot seed injection to spawn new backtests for the
        MR/TF/VB/SR strategy families. Each row is combined with all four families
        (so 4 symbols → 16 seed rows per cycle). Blank fields fall back to
        20 SL / 40 TP / 1.0% risk / 1 max position.
      </div>

      <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4">
        <table className="w-full text-sm" data-testid="seed-symbols-table">
          <thead>
            <tr className="text-[10px] mono uppercase tracking-widest text-[#64748B] border-b border-[#2A2D3A]">
              <th className="text-left py-2 font-normal">Symbol</th>
              <th className="text-right py-2 font-normal">SL Pips</th>
              <th className="text-right py-2 font-normal">TP Pips</th>
              <th className="text-right py-2 font-normal">Risk %</th>
              <th className="text-right py-2 font-normal">Max Positions</th>
              <th className="text-right py-2 font-normal w-16"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-[#2A2D3A]/40">
                <td className="py-2">
                  <input
                    data-testid={`seed-symbol-input-${i}`}
                    className="w-32 bg-[#0B0C10] border border-[#2A2D3A] rounded px-2 py-1 mono text-sm text-[#F1F5F9] focus:border-[#D6FF38] outline-none"
                    value={r.symbol}
                    onChange={(e) => update(i, "symbol", e.target.value)}
                    placeholder="EURUSD"
                  />
                </td>
                <td className="py-2 text-right">
                  <input
                    data-testid={`seed-sl-input-${i}`}
                    type="number" min="1"
                    className="w-20 bg-[#0B0C10] border border-[#2A2D3A] rounded px-2 py-1 mono text-sm text-right text-[#F1F5F9] focus:border-[#D6FF38] outline-none"
                    value={r.stop_loss_pips}
                    onChange={(e) => update(i, "stop_loss_pips", e.target.value)}
                  />
                </td>
                <td className="py-2 text-right">
                  <input
                    data-testid={`seed-tp-input-${i}`}
                    type="number" min="1"
                    className="w-20 bg-[#0B0C10] border border-[#2A2D3A] rounded px-2 py-1 mono text-sm text-right text-[#F1F5F9] focus:border-[#D6FF38] outline-none"
                    value={r.take_profit_pips}
                    onChange={(e) => update(i, "take_profit_pips", e.target.value)}
                  />
                </td>
                <td className="py-2 text-right">
                  <input
                    data-testid={`seed-risk-input-${i}`}
                    type="number" step="0.1" min="0.1"
                    className="w-20 bg-[#0B0C10] border border-[#2A2D3A] rounded px-2 py-1 mono text-sm text-right text-[#F1F5F9] focus:border-[#D6FF38] outline-none"
                    value={r.risk_percent}
                    onChange={(e) => update(i, "risk_percent", e.target.value)}
                  />
                </td>
                <td className="py-2 text-right">
                  <input
                    data-testid={`seed-maxpos-input-${i}`}
                    type="number" min="1"
                    className="w-20 bg-[#0B0C10] border border-[#2A2D3A] rounded px-2 py-1 mono text-sm text-right text-[#F1F5F9] focus:border-[#D6FF38] outline-none"
                    value={r.max_positions}
                    onChange={(e) => update(i, "max_positions", e.target.value)}
                  />
                </td>
                <td className="py-2 text-right">
                  <button
                    data-testid={`seed-remove-${i}`}
                    onClick={() => removeRow(i)}
                    className="text-[#FF4D4D]/70 hover:text-[#FF4D4D] p-1"
                    aria-label="Remove row"
                  >
                    <TrashIcon size={16} />
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan="6" className="py-8 text-center text-xs mono text-[#64748B]">
                  no symbols configured — add one below
                </td>
              </tr>
            )}
          </tbody>
        </table>

        <div className="flex items-center gap-2 mt-3">
          <button
            data-testid="seed-add-row"
            onClick={addRow}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-[#2A2D3A] rounded text-xs mono uppercase tracking-widest text-[#D6FF38] hover:border-[#D6FF38]/40"
          >
            <PlusIcon size={12} /> add symbol
          </button>
          <button
            data-testid="seed-reset-defaults"
            onClick={resetDefaults}
            className="flex items-center gap-1.5 px-3 py-1.5 border border-[#2A2D3A] rounded text-xs mono uppercase tracking-widest text-[#94A3B8] hover:text-[#F1F5F9]"
          >
            <ArrowClockwiseIcon size={12} /> reset defaults
          </button>
        </div>
      </div>

      <div className="border border-[#2A2D3A] bg-[#15171E] rounded-md p-4 space-y-3">
        <div>
          <div className="text-[10px] mono uppercase tracking-widest text-[#64748B] mb-1.5">Bridge Token</div>
          <input
            data-testid="seed-bridge-token"
            type="password"
            className="w-full bg-[#0B0C10] border border-[#2A2D3A] rounded px-3 py-2 mono text-sm text-[#F1F5F9] focus:border-[#D6FF38] outline-none"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="qf_..."
          />
          <div className="text-[10px] mono text-[#64748B] mt-1">
            same BRIDGE_TOKEN your VPS bridge_agent.py uses. Stored in browser localStorage after first save.
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="seed-save"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-[#D6FF38] text-[#0B0C10] rounded text-xs mono uppercase tracking-widest hover:opacity-90 disabled:opacity-40"
          >
            <FloppyDiskIcon size={14} /> {saving ? "saving…" : "save config"}
          </button>
          {message && (
            <div
              data-testid="seed-message"
              className={`flex items-center gap-1.5 text-xs mono ${message.kind === "ok" ? "text-[#2ECA7F]" : "text-[#FF4D4D]"}`}
            >
              {message.kind === "ok" ? <CheckCircleIcon size={14} /> : <WarningIcon size={14} />}
              {message.text}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
